import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap, map, catchError, throwError } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import type { AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { APP_PUBLIC_CONFIG } from '#core';
import { OktaAuthService, LoggingService, UserSessionService } from '#services';
import { environment } from '#environments';

/**
 * Attaches a Bearer token to every request targeting the ARES API.
 * Priority: Okta access_token (direct JWT) → Entra ID via MSAL silent acquisition.
 * Requests to other origins are forwarded without modification.
 * Attaches a Bearer token to every request targeting the ARES API.
 * Priority: Okta access_token (direct JWT) → Entra ID via MSAL silent acquisition.
 * Requests to other origins are forwarded without modification.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) {
    return next(req);
  }

  const oktaService = inject(OktaAuthService);
  const msalService = inject(MsalService);
  const config = inject(APP_PUBLIC_CONFIG);
  const log = inject(LoggingService);
  const sessionService = inject(UserSessionService);

  // Okta user: use the access_token stored by oidc-client-ts.
  const oktaToken = oktaService.accessToken();
  if (oktaToken) {
    log.debug('AuthInterceptor', `okta token attached — ${req.method} ${req.url}`);
    return next(req.clone({ setHeaders: { Authorization: `Bearer ${oktaToken}` } }));
  }

  // Entra user: acquire token silently via MSAL.
  const account =
    msalService.instance.getActiveAccount() ??
    msalService.instance.getAllAccounts()[0];

  if (!account) {
    log.warn('AuthInterceptor', `no account — sending unauthenticated: ${req.method} ${req.url}`);
    return next(req);
  }

  const silentRequest: SilentRequest = {
    account,
    scopes: [config.apiScopeUri],
  };

  return msalService.acquireTokenSilent(silentRequest).pipe(
    map((result: AuthenticationResult) => result.accessToken),
    switchMap(token => {
      log.debug('AuthInterceptor', `entra token acquired — ${req.method} ${req.url}`);
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const activeRoleId = sessionService.activeRoleId();
      if (activeRoleId) {
        headers['X-Active-Role'] = activeRoleId;
      }
      return next(req.clone({ setHeaders: headers }));
    }),
    catchError(err => {
      log.error('AuthInterceptor', `acquireTokenSilent failed — ${req.method} ${req.url}`, {
        errorCode: err?.errorCode,
        message: err?.message,
      });
      return throwError(() => err);
    }),
  );
};
